import {
  AlertController,
  IonButton,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { arrowBackOutline, arrowForwardOutline } from 'ionicons/icons';

import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TranslateService, TranslateModule } from '@ngx-translate/core';

import { Observable, Subscription, BehaviorSubject, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { DateTime } from 'luxon';

import { LeaderboardService } from 'projects/tools/src/lib/shared-module/leaderboard/leaderboard.service';
import { FrontendLeaderboardEntry } from 'projects/firebase/functions/src/types/backend-leaderboard-entry';
import { TimeFromMSPipe } from '../helpers/time-from-ms.pipe';

@Component({
  selector: 'lib-app-leaderboard-review',
  templateUrl: './leaderboard-review.component.html',
  styleUrls: ['./leaderboard-review.component.scss'],
  standalone: true,
  imports: [
    IonButton,
    IonCheckbox,
    IonCol,
    IonContent,
    IonFooter,
    IonGrid,
    IonIcon,
    IonInput,
    IonItem,
    IonRow,
    IonSelect,
    IonSelectOption,
    IonSpinner,
    IonText,
    FormsModule,
    TranslateModule,
    CommonModule,
    TimeFromMSPipe,
  ],
})
export class LeaderboardReviewComponent implements OnInit, OnDestroy {
  private lifecycleSubscriptions: Subscription[];

  public smallProgress = false;
  public currentLang: string;

  public leaderboardTimeLocal = '';
  public leaderboardDateLocal = '';

  // indicates if the puzzle was finished
  private leaderboardCollectionsSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public readonly leaderboardCollections$: Observable<any[]> = this.leaderboardCollectionsSubject.asObservable();
  private leaderboardCollections: any;
  private leaderboardCollectionsOrder: string[];

  public password: string = '';
  public passwordIsValid = false;
  public errorMessage = '';
  public selectedCollectionId = '';
  public selectedCollectionListItem: any;
  public selectedCollectionEntries: FrontendLeaderboardEntry[] = [];

  public changedCollectionEntries = {
    ok: {},
    profane: {},
  };

  public nextIsDisabled = false;
  public backIsDisabled = false;

  public spinner = false;

  public mode = 'ok';

  constructor(
    private translate: TranslateService,
    private alerts: AlertController,
    public leaderboard: LeaderboardService
  ) {
    addIcons({
      arrowBackOutline,
      arrowForwardOutline,
    });
  }

  ngOnInit() {
    this.currentLang = this.translate.currentLang.indexOf('de-') === 0 ? 'de-DE' : 'en-US';

    this.smallProgress = true;

    this.lifecycleSubscriptions = [];
  }

  ngOnDestroy(): void {
    this.lifecycleSubscriptions.forEach(
      (subscription) => subscription && subscription.unsubscribe && subscription.unsubscribe()
    );
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent) {
    if (event.code === 'ArrowLeft' && !this.nextIsDisabled) {
      this.loadNextCollection();
      event.preventDefault();
    }
    if (event.code === 'ArrowRight' && !this.backIsDisabled) {
      this.loadPreviousCollection();
      event.preventDefault();
    }
  }

  updateLeaderboardEntries(leaderboardContext, mode) {}

  submitPassword() {
    this.spinner = true;
    this.clearSelected();
    this.leaderboard
      .getAdminCollections(this.password)
      .pipe(
        map((result: any[]) =>
          result.map((collection) => {
            return {
              humanDate: this.leaderboard.humanDate(DateTime.fromISO(collection.startDate)),
              ...collection,
            };
          })
        )
        //tap((collections) => console.log(collections))
      )
      .subscribe(
        (collections) => {
          this.leaderboardCollections = {};
          this.leaderboardCollectionsOrder = [];
          const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
          collections.sort((a, b) => naturalCollator.compare(a.startDate, b.startDate));
          collections.reverse();
          collections.forEach((collection) => {
            this.leaderboardCollections[collection.id] = collection;
            this.leaderboardCollectionsOrder.push(collection.id);
          });
          this.passwordIsValid = true;
          this.errorMessage = '';
          this.leaderboardCollectionsSubject.next(collections as any[]);
          if (collections.length > 0) {
            this.loadCollection(collections.at(0)['id']);
          } else {
            this.spinner = false;
            this.backIsDisabled = true;
            this.nextIsDisabled = true;
          }
        },
        async (error) => {
          this.spinner = false;
          console.log('encountered error', error);
          console.log(error);
          if (`${error}`.indexOf('permission-denied') > -1) {
            this.errorMessage = await firstValueFrom(
              this.translate.get(`PUZZLE.leaderboard_review.error.wrong_password`)
            );
          } else {
            this.errorMessage = await firstValueFrom(
              this.translate.get(`PUZZLE.leaderboard_review.error.backend`, { error: error })
            );
          }
        }
      );
  }

  selectCollection(ev) {
    this.loadCollection(ev.detail.value);
  }

  loadCollection(collectionId: string) {
    this.spinner = true;
    this.selectedCollectionId = collectionId;
    this.selectedCollectionListItem = this.leaderboardCollections[this.selectedCollectionId];
    this.backIsDisabled = this.leaderboardCollectionsOrder.indexOf(collectionId) === 0;
    this.nextIsDisabled =
      this.leaderboardCollectionsOrder.indexOf(collectionId) === this.leaderboardCollectionsOrder.length - 1;
    this.leaderboard
      .getAdminCollectionEntries(this.password, collectionId)
      .pipe
      // tap((collections) => console.log(collections))
      ()
      .subscribe(
        (entries) => {
          this.selectedCollectionEntries = entries;
          this.spinner = false;
        },
        (error) => {
          this.spinner = false;
          console.log(error);
        }
      );
  }

  async markAll(current_mode: string) {
    let alertHeader, alertMessage, alertButtonCancel, alertButtonOk;
    const selectedCount = Object.values(this.changedCollectionEntries[current_mode]).filter((val) => val).length;

    // This means we are looking at profane and want to flag as ok.
    if (current_mode === 'profane') {
      alertHeader = await firstValueFrom(this.translate.get(`PUZZLE.leaderboard_review.alert_ok.title`));
      alertMessage = await firstValueFrom(
        this.translate.get(`PUZZLE.leaderboard_review.alert_ok.message`, { selectedCount })
      );
      alertButtonCancel = await firstValueFrom(this.translate.get(`PUZZLE.leaderboard_review.alert_ok.button_cancel`));
      alertButtonOk = await firstValueFrom(this.translate.get(`PUZZLE.leaderboard_review.alert_ok.button_ok`));
      // This means we are looking at ok and want to flag as profane.
    } else {
      alertHeader = await firstValueFrom(this.translate.get(`PUZZLE.leaderboard_review.alert_profane.title`));
      alertMessage = await firstValueFrom(
        this.translate.get(`PUZZLE.leaderboard_review.alert_profane.message`, { selectedCount })
      );
      alertButtonCancel = await firstValueFrom(
        this.translate.get(`PUZZLE.leaderboard_review.alert_profane.button_cancel`)
      );
      alertButtonOk = await firstValueFrom(this.translate.get(`PUZZLE.leaderboard_review.alert_profane.button_ok`));
    }
    const alert = await this.alerts.create({
      header: alertHeader,
      message: alertMessage,
      buttons: [
        {
          text: alertButtonCancel,
          handler: async () => {
            alert.dismiss(true);
            return false;
          },
        },
        {
          text: alertButtonOk,
          handler: async () => {
            this.spinner = true;
            for (let [entryId, _] of Object.entries(this.changedCollectionEntries[current_mode]).filter(
              ([key, val]) => val
            )) {
              const entry = this.selectedCollectionEntries.find((entry) => entry.id === entryId);
              await firstValueFrom(
                this.leaderboard.addNicknameExemption(
                  this.password,
                  entry,
                  // If we are viewing ok entries, we need to add to forbidden denied list.
                  current_mode === 'ok'
                )
              );
              await firstValueFrom(
                this.leaderboard.updateCollectionEntry(this.password, this.selectedCollectionId, entry.id, {
                  ...entry,
                  // If we are viewing ok entries, we need to flag as profane and vice versa.
                  profanity: current_mode === 'ok',
                })
              );
            }
            this.loadCollection(this.selectedCollectionId);
            await alert.dismiss(false);
            return false;
          },
        },
      ],
    });

    await alert.present();
  }

  loadPreviousCollection() {
    this.loadCollection(
      this.leaderboardCollectionsOrder[this.leaderboardCollectionsOrder.indexOf(this.selectedCollectionId) - 1]
    );
  }

  loadNextCollection() {
    this.loadCollection(
      this.leaderboardCollectionsOrder[this.leaderboardCollectionsOrder.indexOf(this.selectedCollectionId) + 1]
    );
  }

  changeMode(mode: string) {
    this.clearSelected();
    this.mode = mode;
  }

  clearSelected() {
    this.changedCollectionEntries = {
      ok: {},
      profane: {},
    };
  }

  selectAll(current_mode: string) {
    this.selectedCollectionEntries.forEach((entry: FrontendLeaderboardEntry) => {
      if (current_mode === 'ok' && !entry.profanity) {
        this.changedCollectionEntries[current_mode][entry.id] = true;
      } else if (current_mode === 'profane' && entry.profanity) {
        this.changedCollectionEntries[current_mode][entry.id] = true;
      }
    });
  }

  deselectAll(current_mode: string) {
    Object.entries(this.changedCollectionEntries[current_mode]).forEach(([id, _]) => {
      this.changedCollectionEntries[current_mode][id] = false;
    });
  }

  mark(entry: FrontendLeaderboardEntry, mode) {
    this.changedCollectionEntries[mode][entry.id] = !this.changedCollectionEntries[mode][entry.id];
  }
}
